/* Canonical Phase-gate security evidence strip — reads the SAME artifact API everywhere
   (Security Assurance Center, Due Diligence Rooms, Enterprise Readiness Center).
   Hash integrity is server-verified on every read; nothing is duplicated. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheck, BadgeCheck, AlertTriangle } from "lucide-react";

export const SecurityGateEvidence = ({ compact = false }) => {
  const [arts, setArts] = useState(null);
  useEffect(() => {
    api.get("/compliance/security-evidence").then(({ data }) => setArts(data.artifacts || [])).catch(() => setArts([]));
  }, []);
  if (!arts) return null;
  const current = arts.find((a) => !a.superseded_by) || arts[0];
  if (!current) return null;
  const verified = current.hash_verified;
  return (
    <div className="rounded-2xl bg-white border border-slate-100 p-4" data-testid="security-gate-evidence">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <span className="font-heading font-bold text-navy text-sm" data-testid="sge-title">
            Phase {current.phase} Production Security Gate — Internal Re-Audit {current.status === "PASS" ? "Passed" : current.status}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className={`px-2 py-0.5 rounded-full font-bold ${verified ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="sge-hash">
            {verified ? <BadgeCheck className="w-3 h-3 inline mr-0.5" /> : <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
            SHA-256 {verified ? "Verified" : "FAILED"}
          </span>
          <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">{current.classification}</span>
        </div>
      </div>
      {!compact && (
        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-slate-500">
          <div><b className="text-navy">Artifact</b><br />{current.artifact_id} · v{current.version}</div>
          <div><b className="text-navy">Completed</b><br />{(current.completed_at || "").slice(0, 10)}</div>
          <div><b className="text-navy">Status</b><br />{current.status}</div>
          <div><b className="text-navy">Reviewer</b><br />{current.reviewer || current.created_by}</div>
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-2">Internal phase-gate audit evidence — not an external certification or attestation. Canonical artifact read live; integrity re-verified on every request.</p>
    </div>
  );
};
